<script>
    import { onMount } from 'svelte';
    import { t } from 'svelte-i18n';
    import { fetchCategorias, getCategoriasForDropdown } from '../controllers/categoriaController.js';
    import { fetchProductos } from '../controllers/productoController.js';

    // State
    let categorias = [];
    let productos = [];
    let categoriaActiva = '';
    let subcategorias = [];
    let productosCategoria = [];
    let cargando = false;

    // Cargar datos al montar el componente
    onMount(async () => {
        await cargarCategorias();
        await cargarProductos();
    });

    async function cargarCategorias() {
        try {
            const response = await fetchCategorias(1, 1000);
            if (response && response.categoria) {
                categorias = response.categoria;
                // Filtrar solo categorías raíz (sin parent)
                categorias = categorias.filter(cat => !cat.parent);
            }
        } catch (error) {
            console.error('Error cargando categorías:', error);
        }
    }

    async function cargarProductos() {
        try {
            cargando = true;
            const response = await fetchProductos(1, 1000);
            if (response && response.producto) {
                productos = response.producto;
            }
        } catch (error) {
            console.error('Error cargando productos:', error);
        } finally {
            cargando = false;
        }
    }

    async function seleccionarCategoria(categoriaId) {
        categoriaActiva = categoriaId;
        
        // Cargar subcategorías
        await cargarSubcategorias(categoriaId);
        
        // Filtrar productos por categoría
        await filtrarProductosPorCategoria(categoriaId);
    }

    async function cargarSubcategorias(categoriaId) {
        try {
            const response = await fetchCategorias(1, 1000);
            if (response && response.categoria) {
                subcategorias = response.categoria.filter(cat => 
                    cat.parent && cat.parent.id === categoriaId
                );
            }
        } catch (error) {
            console.error('Error cargando subcategorías:', error);
            subcategorias = [];
        }
    }

    async function filtrarProductosPorCategoria(categoriaId) {
        try {
            cargando = true;
            productosCategoria = productos.filter(producto => 
                producto.categorias && producto.categorias.some(cat => 
                    cat.categoria && cat.categoria.id === categoriaId
                )
            );
        } catch (error) {
            console.error('Error filtrando productos:', error);
            productosCategoria = [];
        } finally {
            cargando = false;
        }
    }

    function obtenerCategoriasProducto(producto) {
        if (!producto.categorias || producto.categorias.length === 0) {
            return 'Sin categorías';
        }
        return producto.categorias.map(cat => cat.categoria.descrip).join(', ');
    }

    function volverACategorias() {
        categoriaActiva = '';
        subcategorias = [];
        productosCategoria = [];
    }
</script>

<div class="container mx-auto px-4 py-8">
    <div class="bg-white rounded-lg shadow-lg p-6">
        <h1 class="text-3xl font-bold text-gray-800 mb-6">{$t('categories.categoryNavigation')}</h1>
        
        {#if !categoriaActiva}
            <!-- Vista de Categorías Principales -->
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {#each categorias as categoria}
                    <div 
                        class="bg-gradient-to-br from-blue-50 to-indigo-100 rounded-lg p-6 border border-blue-200 hover:shadow-lg transition duration-300 cursor-pointer"
                        on:click={() => seleccionarCategoria(categoria.id)}
                        on:keydown={(e) => e.key === 'Enter' && seleccionarCategoria(categoria.id)}
                        role="button"
                        tabindex="0"
                    >
                        <div class="text-center">
                            <div class="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
                                <i class="fas fa-folder text-white text-2xl"></i>
                            </div>
                            <h3 class="text-xl font-semibold text-gray-800 mb-2">{categoria.descrip}</h3>
                            <p class="text-gray-600 text-sm">{$t('categories.clickToExplore')}</p>
                        </div>
                    </div>
                {/each}
            </div>
        {:else}
            <!-- Vista de Categoría Seleccionada -->
            <div class="mb-6">
                <button 
                    on:click={volverACategorias}
                    class="flex items-center text-blue-600 hover:text-blue-800 mb-4"
                >
                    <i class="fas fa-arrow-left mr-2"></i>
                    {$t('categories.backToCategories')}
                </button>
                
                <h2 class="text-2xl font-bold text-gray-800 mb-4">
                    {categorias.find(cat => cat.id === categoriaActiva)?.descrip}
                </h2>
            </div>

            <!-- Subcategorías -->
            {#if subcategorias.length > 0}
                <div class="mb-8">
                    <h3 class="text-lg font-semibold text-gray-700 mb-4">{$t('categories.subcategories')}</h3>
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                        {#each subcategorias as subcategoria}
                            <div 
                                class="bg-gray-50 rounded-lg p-4 border border-gray-200 hover:shadow-md transition duration-200 cursor-pointer"
                                on:click={() => seleccionarCategoria(subcategoria.id)}
                                on:keydown={(e) => e.key === 'Enter' && seleccionarCategoria(subcategoria.id)}
                                role="button"
                                tabindex="0"
                            >
                                <div class="text-center">
                                    <i class="fas fa-folder-open text-gray-500 text-xl mb-2"></i>
                                    <p class="text-sm font-medium text-gray-700">{subcategoria.descrip}</p>
                                </div>
                            </div>
                        {/each}
                    </div>
                </div>
            {/if}

            <!-- Productos de la Categoría -->
            <div>
                <h3 class="text-lg font-semibold text-gray-700 mb-4">
                    {$t('categories.products')} ({productosCategoria.length})
                </h3>
                
                {#if cargando}
                    <div class="flex justify-center items-center py-8">
                        <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                        <span class="ml-2 text-gray-600">{$t('categories.loadingProducts')}</span>
                    </div>
                {:else if productosCategoria.length === 0}
                    <div class="text-center py-8">
                        <i class="fas fa-box-open text-gray-400 text-4xl mb-4"></i>
                        <p class="text-gray-500 text-lg">{$t('categories.noProductsInCategory')}</p>
                    </div>
                {:else}
                    <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {#each productosCategoria as producto}
                            <div class="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition duration-200">
                                <div class="flex justify-between items-start mb-2">
                                    <h4 class="text-lg font-semibold text-gray-800">{producto.nombre}</h4>
                                    <span class="text-lg font-bold text-green-600">${producto.precio}</span>
                                </div>
                                
                                <p class="text-gray-600 text-sm mb-3 line-clamp-2">{producto.descrip}</p>
                                
                                <div class="mb-3">
                                    <span class="text-xs font-medium text-gray-500 uppercase tracking-wide">{$t('categories.categories')}:</span>
                                    <p class="text-sm text-gray-700 mt-1">{obtenerCategoriasProducto(producto)}</p>
                                </div>
                                
                                {#if producto.firstImage}
                                    <div class="mb-3">
                                        <img 
                                            src={producto.firstImage.url} 
                                            alt={producto.nombre}
                                            class="w-full h-32 object-cover rounded-md"
                                        />
                                    </div>
                                {/if}
                                
                                <div class="flex justify-between items-center">
                                    <span class="text-xs text-gray-500">
                                        {$t('common.status')}: {producto.sta ? $t('categories.active') : $t('categories.inactive')}
                                    </span>
                                    {#if producto.parent}
                                        <span class="text-xs text-blue-600">
                                            Subproducto de: {producto.parent.nombre}
                                        </span>
                                    {/if}
                                </div>
                            </div>
                        {/each}
                    </div>
                {/if}
            </div>
        {/if}
    </div>
</div>

<style>
    .line-clamp-2 {
        display: -webkit-box;
        -webkit-line-clamp: 2;
        line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
    }
</style>
